import classNames from "classnames";
import { useRouter } from "next/router";
import * as React from "react";
import Slick from "react-slick";
import type { UsersShopCalendarDayType } from "@graphql/__generated__/types";
import { ShopReservabilityTypeEnum } from "@graphql/__generated__/types";
import useIsPc from "@hooks/useIsPc";
import useIsPcByUA from "@hooks/useIsPcByUA";
import { paths } from "@libs/paths";
import { IFImg } from "@libs/utils/IFImg";
import {
  convertDayjs,
  formats,
  isSaturday,
  isSunday,
  toLocaleStr,
} from "@libs/utils/date";
import { GTMEvent, GTMEvents } from "@libs/utils/gtm";
import { launchTel } from "@libs/utils/url";
import { LoadingSpinner } from "./LoadingSpinner";
import { createSlideSetting } from "./SlideSettings";
import styles from "./index.module.css";
import type { ShopCardLarge_ShopFragment as ShopListItemFragment } from "../../__generated__/fragments";

type Props = {
  shop: ShopListItemFragment;
  calendarDays: UsersShopCalendarDayType[];
  hasMoreCalendarDays: boolean;
  isLoadingCalendarDays: boolean;
  showsAllCalendarDays: boolean;
  onLoadMoreCalendarDays: () => Promise<void>;
};

const getCalendarPages = (
  calendarDays: UsersShopCalendarDayType[],
  isPC: boolean,
  showsAllCalendarDays: boolean
): UsersShopCalendarDayType[][] => {
  const numberOfSchedulesPerPage = isPC ? 12 : 7;
  const allPageCount = Math.ceil(
    calendarDays.length / numberOfSchedulesPerPage
  );
  const numberOfPages = showsAllCalendarDays ? allPageCount : 2;

  const pages: UsersShopCalendarDayType[][] = [];

  for (let i = 0; i < numberOfPages; i++) {
    const page = calendarDays.slice(
      i * numberOfSchedulesPerPage,
      (i + 1) * numberOfSchedulesPerPage
    );
    pages.push(page);
  }

  return pages;
};

export const Calendar: React.FC<Props> = ({
  shop,
  calendarDays,
  hasMoreCalendarDays,
  isLoadingCalendarDays,
  showsAllCalendarDays,
  onLoadMoreCalendarDays,
}) => {
  const sliderRef = React.useRef<Slick | null>(null);
  const router = useRouter();
  const { isPcByUA } = useIsPcByUA.useContainer();
  const isPc = useIsPc.useContainer();

  if (!shop.isReservableOnline) {
    return null;
  }

  const pages = getCalendarPages(
    calendarDays,
    isPc ?? false,
    showsAllCalendarDays
  );
  const handleFirstNextArrowClick = async () => {
    if (!hasMoreCalendarDays || isLoadingCalendarDays) {
      return false;
    }

    await onLoadMoreCalendarDays();

    // 追加取得後にレイアウトが崩れるため再描画とslickの再レイアウトを待つ
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve());
      });
    });

    sliderRef.current?.slickNext();

    return true;
  };

  const slideSetting = createSlideSetting({
    canLoadMore: hasMoreCalendarDays,
    isLoading: isLoadingCalendarDays,
    onInterceptNextClick: handleFirstNextArrowClick,
  });

  const handleScheduleClick = (calendarDay: UsersShopCalendarDayType) => {
    if (calendarDay.reservability === ShopReservabilityTypeEnum.Reservable) {
      GTMEvent(
        GTMEvents.toReservationFormPressed(
          "予約スケジュール",
          shop.uuid,
          undefined,
          toLocaleStr(calendarDay.date, "ymdHyphen")
        )
      );

      router.push(
        paths.shopRequestReservation(shop.uuid, {
          date: calendarDay.date,
          matchId: calendarDay.defaultReservableMatchId ?? undefined,
        })
      );
    } else if (
      calendarDay.reservability === ShopReservabilityTypeEnum.Tel &&
      !isPcByUA
    ) {
      launchTel(shop.tel, "予約スケジュール");
    }
  };

  const statusComponentMap: {
    [key in ShopReservabilityTypeEnum]: React.ReactElement;
  } = {
    [ShopReservabilityTypeEnum.Reservable]: (
      <div className={classNames(styles.tablesStatus, styles.tablesAvailable)}>
        <img
          className={styles.availableImage}
          src={IFImg("/img/icon_ok_circle.svg")}
        ></img>
      </div>
    ),
    [ShopReservabilityTypeEnum.Tel]: (
      <div
        className={classNames(
          styles.tablesStatus,
          !isPcByUA && styles.tablesAvailable
        )}
      >
        TEL
      </div>
    ),
    [ShopReservabilityTypeEnum.Full]: (
      <div className={classNames(styles.tablesStatus)}>満席</div>
    ),
    [ShopReservabilityTypeEnum.None]: (
      <div className={classNames(styles.tablesStatus)}>-</div>
    ),
  };

  return (
    <div className={styles.bookingCalendarWrap}>
      <Slick
        ref={sliderRef}
        {...slideSetting}
        className={styles.bookingCalendar}
      >
        {pages.map((calendarDays, pageIndex) => {
          const isLastPage = pageIndex === pages.length - 1;
          const shouldApplyLastCalendarPadding =
            isLastPage && (isPc || !hasMoreCalendarDays);

          return (
            <div
              className={classNames(
                styles.calendar,
                shouldApplyLastCalendarPadding && styles.lastCalendar
              )}
              key={pageIndex}
            >
              <ul className={styles.calendarList}>
                {calendarDays.map((cd) => {
                  const day = convertDayjs(cd.date);

                  return (
                    <li
                      className={classNames(
                        styles.calendarItem,
                        (cd.reservability ===
                          ShopReservabilityTypeEnum.Reservable ||
                          (cd.reservability === ShopReservabilityTypeEnum.Tel &&
                            !isPcByUA)) &&
                          styles.calendarItemAvailable
                      )}
                      key={cd.date}
                      onClick={() => handleScheduleClick(cd)}
                    >
                      <div
                        className={classNames(
                          styles.dayOfWeek,
                          isSunday(day) || cd.isHoliday
                            ? styles.sun
                            : isSaturday(day) && styles.sat
                        )}
                      >
                        {day.format(formats.weekOnly)}
                      </div>
                      <div className={styles.day}>
                        {toLocaleStr(day, "dateOnlyEn")}
                      </div>
                      {statusComponentMap[cd.reservability]}
                      {cd.hasMatch && (
                        <div className={styles.broadcastStatus}>上映</div>
                      )}
                    </li>
                  );
                })}
                {!isPc &&
                  hasMoreCalendarDays &&
                  pageIndex === pages.length - 1 && (
                    <li
                      className={classNames(
                        styles.calendarItem,
                        styles.loadMoreCalendarItem
                      )}
                    >
                      {isLoadingCalendarDays ? (
                        <div
                          className={styles.loadMoreCalendarLoading}
                          aria-label="読み込み中"
                        >
                          <LoadingSpinner
                            className={styles.loadMoreCalendarSpinner}
                          />
                        </div>
                      ) : (
                        <button
                          type="button"
                          className={styles.loadMoreCalendarButton}
                          onClick={() => onLoadMoreCalendarDays()}
                          aria-label="さらに読み込む"
                        >
                          <span className={styles.loadMoreCalendarArrowWrap}>
                            <img
                              src={IFImg("/img/icon_arrow_wh_active.svg")}
                              width={16}
                              height={16}
                              alt=""
                            />
                          </span>
                        </button>
                      )}
                    </li>
                  )}
              </ul>
            </div>
          );
        })}
      </Slick>
    </div>
  );
};
